import { getApiData, listApiData, postApiData } from "@hooks/utils/api";
import { addSentryBreadcrumb, catchSentryError } from "@utils/sentry";
import { getImageUrl } from "@utils/storage";
import { useCallback, useEffect, useState } from "react";
import {
  decodeProfile,
  encodeProfile
} from "catchment-common/decoders/profile";
import { Profile } from "catchment-common/entities/profile";
import { ProfileLineItem } from "catchment-common/interfaces/profile";
import { showToast } from "@utils/toast";

export const useProfile = (organisationId?: string) => {
  const [profiles, setProfiles] = useState<ProfileLineItem[]>([]);
  const [isProfileLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const handleApiError = useCallback((e: any, errorMessage: string) => {
    catchSentryError(e);
    setError(errorMessage);
  }, []);

  const getProfile = useCallback(
    async (
      id: string,
      auth: boolean
    ): Promise<{ profile?: ProfileLineItem; success: boolean }> => {
      try {
        setIsLoading(true);
        const profileResult = await getApiData("getProfile", "profile", id);

        if (profileResult && profileResult.status !== 200) {
          throw new Error("Could not fetch profile");
        }
        if (
          !profileResult ||
          !profileResult.data ||
          Object.keys(profileResult.data).length === 0
        ) {
          return { success: true };
        }
        return {
          profile: await decodeProfile(
            profileResult.data as Profile,
            getImageUrl
          ),
          success: true
        };
      } catch (e: any) {
        if (auth) {
          // The caller owns the user-facing outcome and turns this into a
          // return value, so this is the last place the real cause exists.
          // Losing it is what made a failed login show "contact support" with
          // nothing in Sentry to investigate.
          //
          // A breadcrumb rather than a capture, because this runs inside
          // getProfileWithRetry's loop: capturing here would report the
          // transient first attempt that the retry then recovers from, and
          // emit an event for a login that went on to succeed. Breadcrumbs
          // only transmit attached to an event, so a recovered failure costs
          // nothing, while a real one rides along on the event the caller
          // raises — carrying the status that says which failure it was.
          addSentryBreadcrumb(
            "getProfile failed",
            "auth",
            {
              message: e?.message,
              status:
                e?.response?.statusCode ??
                e?.$metadata?.httpStatusCode ??
                e?.statusCode
            },
            "error"
          );
        } else {
          handleApiError(e, "Could not fetch profile");
        }
      } finally {
        setIsLoading(false);
      }
      return {
        success: false
      };
    },
    [handleApiError]
  );

  const listProfiles = useCallback(
    async (orgId: string): Promise<void> => {
      try {
        setIsLoading(true);
        const profileResult = await listApiData(
          "listProfiles",
          "profile",
          orgId
        );
        const decodedProfiles = await Promise.all(
          profileResult.data.map((p) =>
            decodeProfile(p as Profile, getImageUrl)
          )
        );
        setProfiles(decodedProfiles);
      } catch (e: any) {
        handleApiError(e, "Could not list profiles");
      } finally {
        setIsLoading(false);
      }
    },
    [handleApiError]
  );

  const searchProfiles = useCallback(
    async (query: string): Promise<ProfileLineItem[]> => {
      try {
        const profileResult = await listApiData(
          "searchProfiles",
          "profile",
          query
        );
        const decodedProfiles = await Promise.all(
          profileResult.data.map((p) =>
            decodeProfile(p as Profile, getImageUrl)
          )
        );
        return decodedProfiles;
      } catch (e: any) {
        handleApiError(e, "Could not search profiles");
        return [];
      }
    },
    [handleApiError]
  );

  const createProfile = useCallback(
    async (profile: ProfileLineItem): Promise<ProfileLineItem> => {
      try {
        setIsLoading(true);
        const encodedProfile = encodeProfile(profile);
        const upsertedProfile = await postApiData(
          "createProfile",
          "profile",
          encodedProfile
        );
        const decodedProfile = await decodeProfile(
          upsertedProfile.data as Profile,
          getImageUrl
        );
        setProfiles((prevProfiles) => [...prevProfiles, decodedProfile]);
        return decodedProfile;
      } catch (e: any) {
        handleApiError(e, "Could not create profile");
      } finally {
        setIsLoading(false);
      }
      return profile;
    },
    [handleApiError]
  );

  const inviteProfile = useCallback(
    async (profile: ProfileLineItem): Promise<void> => {
      try {
        setIsLoading(true);
        const encodedProfile = encodeProfile(profile);
        const response = await postApiData(
          "inviteProfile",
          "profile",
          encodedProfile
        );
        const createdProfile = await decodeProfile(
          response.data as never as Profile,
          getImageUrl
        );
        setProfiles((prevProfiles) => [...prevProfiles, createdProfile]);
      } catch (e: any) {
        handleApiError(e, "Could not invite profile");
      } finally {
        setIsLoading(false);
      }
    },
    [handleApiError]
  );

  const updateProfile = useCallback(
    async (profile: ProfileLineItem): Promise<void> => {
      try {
        setIsLoading(true);
        const encodedProfile = encodeProfile(profile);
        const response = await postApiData(
          "updateProfile",
          "profile",
          encodedProfile
        );
        const updatedProfile = await decodeProfile(
          response.data as never as Profile,
          getImageUrl
        );

        setProfiles((prevProfiles) =>
          prevProfiles.map((p) =>
            p.id === updatedProfile.id ? updatedProfile : p
          )
        );
      } catch (e: any) {
        handleApiError(e, "Could not update profile");
      } finally {
        setIsLoading(false);
      }
    },
    [handleApiError]
  );

  const deleteProfile = useCallback(
    async (profile: ProfileLineItem): Promise<void> => {
      try {
        setIsLoading(true);
        const encodedProfile = encodeProfile(profile);
        await postApiData("deleteProfile", "profile", encodedProfile);
        setProfiles((prevProfiles) =>
          prevProfiles.filter((p) => p.id !== profile.id)
        );
      } catch (e: any) {
        handleApiError(e, "Could not delete profile");
      } finally {
        setIsLoading(false);
      }
    },
    [handleApiError]
  );

  // Admin action: set a temporary password for another org user. The user must
  // choose a new one at next login (backend sets it non-permanent).
  const adminResetPassword = useCallback(
    async (profileId: string, temporaryPassword: string): Promise<boolean> => {
      try {
        setIsLoading(true);
        await postApiData("adminResetPassword", "profile", {
          id: profileId,
          temporaryPassword
        } as never);
        return true;
      } catch (e: any) {
        handleApiError(e, "Could not reset password");
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [handleApiError]
  );

  // Admin action: reset another org user's MFA (un-enrol TOTP + clear flag).
  const adminResetMfa = useCallback(
    async (profileId: string): Promise<boolean> => {
      try {
        setIsLoading(true);
        await postApiData("adminResetMfa", "profile", {
          id: profileId
        } as never);
        return true;
      } catch (e: any) {
        handleApiError(e, "Could not reset MFA");
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [handleApiError]
  );

  // Admin action: re-send the invite email to an org user who never acted on
  // the first one. Issues a fresh password, so it invalidates whatever was in
  // the original email — UserTable confirms before calling.
  const resendProfileInvite = useCallback(
    async (profileId: string): Promise<boolean> => {
      try {
        setIsLoading(true);
        await postApiData("resendProfileInvite", "profile", {
          id: profileId
        } as never);
        showToast("Invite Resent");
        return true;
      } catch (e: any) {
        handleApiError(e, "Could not resend invite");
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [handleApiError]
  );

  useEffect(() => {
    if (organisationId) {
      listProfiles(organisationId);
    }
  }, [organisationId, listProfiles]);

  return {
    adminResetMfa,
    adminResetPassword,
    createProfile,
    deleteProfile,
    error,
    getProfile,
    inviteProfile,
    isProfileLoading,
    listProfiles,
    profiles,
    resendProfileInvite,
    searchProfiles,
    updateProfile
  };
};
